"use strict";
/**
 * Implementación del esquema json del SAT en Typescript
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.Validator = void 0;
const predicates_1 = require("./utils/predicates");
const secure_map_1 = require("./signature/secure_map");
/**
 * Validador de esquemas json del estándar del SAT.
 */
class Validator {
    /**
     * Constructor del validador de esquemas
     * @param json Json que representa el esquema
     */
    constructor(json) {
        // Es la versión menos costosa computacionalmente hablando de find_error
        this.is_valid = (target, source = this.schema) => {
            if (this.find_error(target, source) == null) {
                return true;
            }
            else {
                return false;
            }
        };
        this.schema = json;
    }
    // Da información especifica sobre el error. Null indica que no hubo error
    find_error(target, source = this.schema) {
        if (target == undefined) {
            return {
                display: () => "el elemento es nulo, o indefinido"
            };
        }
        else if ((0, secure_map_1.isSecureMap)(source)) {
            // Podría ser una negación, lo cuál buscamos
            let not = source.get("not");
            if (not) {
                // Estamos ante una negación
                let error = this.find_error(not);
                if (error != null) {
                    return null;
                }
                else {
                    return {
                        display: () => "error de esquema negado",
                        path: Array.from(["not"])
                    };
                }
            }
            let const_val = source.get("const");
            if (const_val) {
                // Ahora verificamos si es un "const"
                if (!(const_val === target)) {
                    return {
                        display: () => `el elemento debería ser igual a ${const_val}`
                    };
                }
                return null;
            }
            else {
                let enum_vals = source.get("enum");
                if (enum_vals) {
                    // Es un enum!
                    if (enum_vals.indexOf(target) == -1) {
                        return {
                            display: () => "el elemento sólo puede tomar valores de la siguiente lista: " + String(enum_vals.join(", "))
                        };
                    }
                    return null;
                }
                else {
                    let tpe = source.get("type");
                    if (tpe != undefined && tpe == "string") {
                        if ((0, predicates_1.isString)(target)) {
                            let minLength = source.get("minLength");
                            if (minLength != undefined && target.length < minLength) {
                                return {
                                    display: () => `la longitud del elemento, ${target.length}, no llega al mínimo, ${minLength}`
                                };
                            }
                            let maxLength = source.get("maxLength");
                            if (maxLength != undefined && maxLength < target.length) {
                                return {
                                    display: () => `la longitud del elemento, ${target.length}, excede el máximo, ${maxLength}`
                                };
                            }
                            let pattern = source.get("pattern");
                            if (pattern != undefined) {
                                if (typeof (pattern) !== 'string') {
                                    return {
                                        display: () => `el campo de patrón del elemento debe ser una cadena`
                                    };
                                }
                                let regexp = new RegExp(pattern);
                                if (!regexp.test(target)) {
                                    return {
                                        display: () => `el patrón del campo no se acata`
                                    };
                                }
                            }
                            return null;
                        }
                        else {
                            return {
                                display: () => "el campo debería ser una cadena"
                            };
                        }
                    }
                    else if (tpe != undefined && tpe == "number") {
                        if ((0, predicates_1.isNumber)(target)) {
                            let minimum = source.get("minimum");
                            if (minimum != undefined && target <= minimum) {
                                return {
                                    display: () => `el elemento , ${target}, no llega al mínimo, ${minimum}`
                                };
                            }
                            let maximum = source.get("maximum");
                            if (maximum != undefined && maximum <= target) {
                                return {
                                    display: () => `el elemento, ${target}, excede el máximo, ${maximum}`
                                };
                            }
                            let minimum_exclusive = source.get("minimumExclusive");
                            if (minimum_exclusive != undefined && target < minimum_exclusive) {
                                return {
                                    display: () => `el elemento , ${target}, no llega al mínimo, ${minimum_exclusive}`
                                };
                            }
                            let maximum_exclusive = source.get("maximumExclusive");
                            if (maximum_exclusive != undefined && maximum_exclusive < target) {
                                return {
                                    display: () => `el elemento, ${target}, excede el máximo, ${maximum_exclusive}`
                                };
                            }
                            return null;
                        }
                        else {
                            return {
                                display: () => "el campo debería ser un número"
                            };
                        }
                    }
                    else if (tpe != undefined && tpe == "integer") {
                        if ((0, predicates_1.isNumber)(target)) {
                            let minimum = source.get("minimum");
                            if (minimum != undefined && target < minimum) {
                                return {
                                    display: () => `el elemento , ${target}, no llega al mínimo, ${minimum}`
                                };
                            }
                            let maximum = source.get("maximum");
                            if (maximum != undefined && maximum < target) {
                                return {
                                    display: () => `el elemento, ${target}, excede el máximo, ${maximum}`
                                };
                            }
                            return null;
                        }
                        else {
                            return {
                                display: () => "el campo debería ser un entero"
                            };
                        }
                    }
                    else if (tpe != undefined && tpe == "boolean") {
                        if ((0, predicates_1.isBoolean)(target)) {
                            return null;
                        }
                        else {
                            return {
                                display: () => "el campo debería ser un booleano"
                            };
                        }
                    }
                    else if (tpe != undefined && tpe == "array") {
                        let items = source.get("items");
                        let contains = source.get("contains");
                        let min_items = source.get("minItems");
                        let max_items = source.get("maxItems");
                        if ((0, predicates_1.isArray)(target)) {
                            if (min_items != undefined && target.length < min_items) {
                                return {
                                    display: () => "el arreglo no alcanza el tamaño mínimo de items",
                                };
                            }
                            if (max_items != undefined && max_items < target.length) {
                                return {
                                    display: () => "el arreglo excede el tamaño mínimo de items",
                                };
                            }
                            if (items) {
                                if ((0, predicates_1.isArray)(items)) {
                                    for (let [idx, elem] of target.entries()) {
                                        if ((0, predicates_1.isString)(elem)) {
                                            if (items.findIndex((item) => item == elem) == -1) {
                                                // Element not found in the array of allowed items
                                                return {
                                                    display: () => `el elemento ${idx} no está contenido dentro de la etiqueta items`
                                                };
                                            }
                                        }
                                        else {
                                            return {
                                                display: () => "todos los items deberían ser cadenas en este caso",
                                            };
                                        }
                                    }
                                }
                                else {
                                    for (let [idx, elem] of target.entries()) {
                                        let error = this.find_error(elem, items);
                                        if (error) {
                                            let path = Array.from([`${idx}-element`]);
                                            if (error.path) {
                                                error.path.push(path[0]);
                                                path = error.path;
                                            }
                                            return {
                                                display: error.display,
                                                path: path
                                            };
                                        }
                                    }
                                }
                            }
                            if (contains) {
                                if ((0, predicates_1.isArray)(contains)) {
                                    let found = false;
                                    for (let [idx, elem] of target.entries()) {
                                        if ((0, predicates_1.isString)(elem)) {
                                            if (contains.findIndex((item) => item == elem) != -1) {
                                                // Element not found in the array of allowed items
                                                found = true;
                                                break;
                                            }
                                        }
                                        else {
                                            return {
                                                display: () => "todos los items deberían ser cadenas en este caso",
                                            };
                                        }
                                    }
                                    if (!found) {
                                        return {
                                            display: () => "No se encontró ni una de las palabras en contained dentro de los elementos",
                                        };
                                    }
                                }
                                else {
                                    let found = false;
                                    for (let [idx, elem] of target.entries()) {
                                        let error = this.find_error(elem, contains);
                                        if (error == null) {
                                            found = true;
                                            break;
                                        }
                                    }
                                    if (!found) {
                                        return {
                                            display: () => "No se encontró ni una de las palabras en contained dentro de los elementos",
                                        };
                                    }
                                }
                            }
                            return null;
                        }
                        else {
                            return {
                                display: () => "el campo debería ser un arreglo"
                            };
                        }
                    }
                    else if (tpe == undefined || tpe == "object") {
                        if ((0, secure_map_1.isSecureMap)(target)) {
                            let properties = source.get("properties");
                            let if_schema = source.get("if");
                            let else_schema = source.get("else");
                            let then_schema = source.get("then");
                            let missing = new Set();
                            let required = source.get("required");
                            if (required != undefined) {
                                for (let key of required) {
                                    missing.add(key);
                                }
                            }
                            if (properties != undefined) {
                                for (let [property_id, value] of target) {
                                    // Quitamos de aquí la etiqueta
                                    missing.delete(property_id);
                                    // Y continuamos con la validación
                                    let property = properties.get(property_id);
                                    if (property != undefined) {
                                        let error = this.find_error(value, property);
                                        if (error) {
                                            let path = Array.from([property_id]);
                                            if (error.path) {
                                                error.path.push(property_id);
                                                path = error.path;
                                            }
                                            return {
                                                display: error.display,
                                                path: path
                                            };
                                        }
                                    }
                                }
                            }
                            if (missing.size != 0) {
                                return {
                                    display: () => "faltan los siguientes campos obligatorios: " + String(Array.from(missing).join(", "))
                                };
                            }
                            if (if_schema != undefined) {
                                if (then_schema == undefined) {
                                    return {
                                        display: () => "existe un if, sin un then en un objeto"
                                    };
                                }
                                let error = this.find_error(target, if_schema);
                                if (error) {
                                    if (else_schema != undefined) {
                                        let else_error = this.find_error(target, else_schema);
                                        if (else_error) {
                                            let text = else_error.display();
                                            return {
                                                display: () => `(condición else) ${text}`,
                                            };
                                        }
                                    }
                                }
                                else {
                                    // Then condition needs to be followed
                                    let then_error = this.find_error(target, then_schema);
                                    if (then_error) {
                                        let text = then_error.display();
                                        return {
                                            display: () => `(condición then) ${text}`,
                                        };
                                    }
                                }
                            }
                            // Todo bien
                            return null;
                        }
                        else {
                            return {
                                display: () => "el campo debería ser un diccionario"
                            };
                        }
                    }
                    else {
                        throw "inconsistent schema, tpe is " + tpe;
                    }
                }
            }
        }
        else {
            throw "Error en el esquema";
        }
    }
}
exports.Validator = Validator;
//# sourceMappingURL=schema.js.map